import Axios, { AxiosResponse } from 'axios';
import { useAtom, useSetAtom } from "jotai";
import { useRef } from "react";
import { environment } from "../../environment";
import { errorCatcher, snackbarSettingsAtom } from "../../hooks";
import { useLogin } from "../login";
import {
    BrowsableDesign,
    CatalogBrand,
    CatalogDesign,
    CatalogFilters,
    CatalogResponse,
    DEFAULT_COLOR_CLOSENESS,
    DesignDetails,
    browseBrandCountsAtom,
    browseCursorAtom,
    browseDesignsAtom,
    browseHasMoreAtom,
    browseLoadingAtom,
    browsePaginationLoadingAtom,
    browseTotalAtom,
    catalogBrandCountsAtom,
    catalogCursorAtom,
    catalogDesignsAtom,
    catalogHasMoreAtom,
    catalogLoadingAtom,
    catalogPaginationLoadingAtom,
    catalogTotalAtom
} from "./types";

export const DESIGNER_CATALOG_PAGE_SIZE = 48;

// Callers pass `append`, never a page number or cursor. The cursor lives in a
// ref inside the hook (see browseCursorRef): handing it to the caller would
// reintroduce the stale-closure race the old plain page counter avoided only by
// accident.

/**
 * Appends a page of results, dropping anything already in the list.
 *
 * The API pages a large ordered set, and any instability in that order (or a row
 * added between requests) can hand back a design we already have. Without this
 * the list grows past the real number of distinct designs, which is what made
 * the accumulated-length `hasMore` check strand the final design.
 *
 * A duplicate id would also break React keys and double-count a brand section,
 * and this is what makes addDesign's optimistic prepend safe against the next
 * page landing.
 *
 * Rows with an undefined id are kept deliberately — filtering them out is what
 * stranded the last design, so do not "simplify" this predicate.
 */
const mergeById = <T extends { id?: number }>(previous: T[], incoming: T[]): T[] => {
    const seen = new Set(previous.map((design) => design.id));
    return [...previous, ...incoming.filter((design) => design.id === undefined || !seen.has(design.id))];
};

type FetchList = (search: string, append: boolean, filters?: CatalogFilters) => Promise<void>;
type AddDesign = (design: BrowsableDesign) => Promise<boolean>;
type RemoveDesign = (designId: number) => Promise<boolean>;
type FetchDesignDetails = (designId: number) => Promise<DesignDetails | null>;
type FetchAvailableBrands = (q?: string, offset?: number, signal?: AbortSignal) => Promise<{ brands: CatalogBrand[]; total: number }>;

type DesignerCatalogFunctions = {
    fetchBrowse: FetchList;
    fetchCatalog: FetchList;
    addDesign: AddDesign;
    removeDesign: RemoveDesign;
    fetchDesignDetails: FetchDesignDetails;
    fetchAvailableBrands: FetchAvailableBrands;
};

type UseDesignerCatalog = [
    BrowsableDesign[],
    CatalogDesign[],
    DesignerCatalogFunctions
];

export const useDesignerCatalog = (): UseDesignerCatalog => {
    const [browseDesigns, setBrowseDesigns] = useAtom(browseDesignsAtom);
    const [catalogDesigns, setCatalogDesigns] = useAtom(catalogDesignsAtom);
    const setBrowseTotal = useSetAtom(browseTotalAtom);
    const setBrowseHasMore = useSetAtom(browseHasMoreAtom);
    const setBrowseLoading = useSetAtom(browseLoadingAtom);
    const setBrowsePaginationLoading = useSetAtom(browsePaginationLoadingAtom);
    const setCatalogTotal = useSetAtom(catalogTotalAtom);
    const setCatalogHasMore = useSetAtom(catalogHasMoreAtom);
    const setCatalogLoading = useSetAtom(catalogLoadingAtom);
    const setCatalogPaginationLoading = useSetAtom(catalogPaginationLoadingAtom);
    const setBrowseBrandCounts = useSetAtom(browseBrandCountsAtom);
    const setCatalogBrandCounts = useSetAtom(catalogBrandCountsAtom);
    const setBrowseCursor = useSetAtom(browseCursorAtom);
    const setCatalogCursor = useSetAtom(catalogCursorAtom);

    const setSnackbar = useSetAtom(snackbarSettingsAtom);
    const [, authToken, loginFunctions] = useLogin();

    // Sequence guards so stale debounced responses can't clobber newer results
    const browseSeqRef = useRef(0);
    const catalogSeqRef = useRef(0);

    // Next-page cursors. Refs, not state: a load-more fired from a callback
    // must read the value written by the response that just landed, and an
    // `append: false` fetch must reset it synchronously.
    const browseCursorRef = useRef<string | null>(null);
    const catalogCursorRef = useRef<string | null>(null);

    const headers = { 'X-Wallmates-Auth': authToken };

    const buildListUrl = (base: string, after: string | null, search: string, filters?: CatalogFilters): string => {
        const params = new URLSearchParams({
            limit: String(DESIGNER_CATALOG_PAGE_SIZE)
        });
        if (after) params.set('after', after);
        if (search.trim().length > 0) params.set('search', search.trim());
        if (filters?.patternTypes && filters.patternTypes.length > 0) {
            params.set('pattern_type', filters.patternTypes.join(','));
        }
        if (filters?.brandIds && filters.brandIds.length > 0) {
            params.set('brand_id', filters.brandIds.join(','));
        }
        if (filters?.sort) params.set('sort', filters.sort);
        // Colour search. Closeness only means anything alongside a base colour,
        // so both params are sent together or not at all.
        if (filters?.colorHex) {
            params.set('color_hex', filters.colorHex);
            params.set('color_closeness', String(filters.colorCloseness ?? DEFAULT_COLOR_CLOSENESS));
        }
        return `${base}?${params.toString()}`;
    };

    const fetchBrowse: FetchList = async (search, append, filters) => {
        // An append with no cursor yet means the first page hasn't landed (or
        // failed). Re-requesting without `after` would refetch page 1 and
        // append it onto itself; the dedupe would swallow the rows but the
        // round trip and the loading flicker are pointless.
        if (append && browseCursorRef.current === null) return;

        const seq = ++browseSeqRef.current;
        const setLoading = append ? setBrowsePaginationLoading : setBrowseLoading;
        setLoading(true);

        // A non-append fetch (new search/filter/sort) invalidates the cursor —
        // a stale cursor against new filters returns the wrong page entirely.
        if (!append) {
            browseCursorRef.current = null;
            setBrowseCursor(null);
        }

        const response: AxiosResponse<CatalogResponse<BrowsableDesign>> | void = await Axios.get(
            buildListUrl(`${environment.designerCatalogUrl}/browse`, append ? browseCursorRef.current : null, search, filters),
            { headers }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (seq !== browseSeqRef.current) return; // stale response — a newer request is in flight

        if (response) {
            const { designs, hasNextPage, endCursor, total, brandCounts } = response.data;
            setBrowseDesigns((previous: BrowsableDesign[]) => append ? mergeById(previous, designs) : designs);
            setBrowseHasMore(hasNextPage);
            browseCursorRef.current = endCursor;
            setBrowseCursor(endCursor);
            // total/brandCounts only come back on the first page.
            if (total !== undefined) setBrowseTotal(total);
            if (brandCounts) setBrowseBrandCounts(brandCounts);
        } else {
            // Stop paginating on failure. The grid re-arms its sentinel every
            // time paginationLoading falls, so leaving hasMore true here would
            // retry the same failed request forever while the sentinel sits in
            // the 400px root margin. A filter/search change refetches and
            // clears this.
            setBrowseHasMore(false);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while loading available designs. Please try again later.'
            });
        }
        setLoading(false);
    };

    const fetchCatalog: FetchList = async (search, append, filters) => {
        // An append with no cursor yet means the first page hasn't landed (or
        // failed). Re-requesting without `after` would refetch page 1 and
        // append it onto itself; the dedupe would swallow the rows but the
        // round trip and the loading flicker are pointless.
        if (append && catalogCursorRef.current === null) return;

        const seq = ++catalogSeqRef.current;
        const setLoading = append ? setCatalogPaginationLoading : setCatalogLoading;
        setLoading(true);

        if (!append) {
            catalogCursorRef.current = null;
            setCatalogCursor(null);
        }

        const response: AxiosResponse<CatalogResponse<CatalogDesign>> | void = await Axios.get(
            buildListUrl(environment.designerCatalogUrl, append ? catalogCursorRef.current : null, search, filters),
            { headers }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (seq !== catalogSeqRef.current) return; // stale response — a newer request is in flight

        if (response) {
            const { designs, hasNextPage, endCursor, total, brandCounts } = response.data;
            setCatalogDesigns((previous: CatalogDesign[]) => append ? mergeById(previous, designs) : designs);
            setCatalogHasMore(hasNextPage);
            catalogCursorRef.current = endCursor;
            setCatalogCursor(endCursor);
            if (total !== undefined) setCatalogTotal(total);
            if (brandCounts) setCatalogBrandCounts(brandCounts);
        } else {
            // Same reasoning as fetchBrowse: don't leave hasMore true on a
            // failed page or the grid's re-arm effect loops on it.
            setCatalogHasMore(false);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while loading your catalog. Please try again later.'
            });
        }
        setLoading(false);
    };

    const setInCatalogFlag = (designId: number, inCatalog: boolean) => {
        setBrowseDesigns((previous: BrowsableDesign[]) =>
            previous.map((design) => design.id === designId ? { ...design, in_catalog: inCatalog } : design)
        );
    };

    const addDesign: AddDesign = async (design) => {
        if (!design.id) return false;
        const designId = design.id;

        // Optimistic: flag the browse card and prepend to the catalog list
        setInCatalogFlag(designId, true);
        setCatalogDesigns((previous: CatalogDesign[]) =>
            previous.some((d) => d.id === designId) ? previous : [design, ...previous]
        );
        setCatalogTotal((previous: number) => previous + 1);

        try {
            await Axios.post(
                `${environment.designerCatalogUrl}/designs`,
                { design_id: designId },
                { headers }
            );
            return true;
        } catch (err: any) {
            if (err?.response?.status === 409) {
                // Already in the catalog — treat as success and keep the flag in sync
                return true;
            }
            // Revert optimistic update
            setInCatalogFlag(designId, false);
            setCatalogDesigns((previous: CatalogDesign[]) => previous.filter((d) => d.id !== designId));
            setCatalogTotal((previous: number) => Math.max(0, previous - 1));
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while adding the design to your catalog. Please try again later.'
            });
            return false;
        }
    };

    const removeDesign: RemoveDesign = async (designId) => {
        // Optimistic: unflag the browse card and drop from the catalog list
        const previousCatalog = catalogDesigns;
        setInCatalogFlag(designId, false);
        setCatalogDesigns((previous: CatalogDesign[]) => previous.filter((d) => d.id !== designId));
        setCatalogTotal((previous: number) => Math.max(0, previous - 1));

        try {
            await Axios.delete(`${environment.designerCatalogUrl}/designs/${designId}`, { headers });
            return true;
        } catch (err: any) {
            // Revert optimistic update
            setInCatalogFlag(designId, true);
            setCatalogDesigns(previousCatalog);
            setCatalogTotal((previous: number) => previous + 1);
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while removing the design from your catalog. Please try again later.'
            });
            return false;
        }
    };

    const fetchDesignDetails: FetchDesignDetails = async (designId) => {
        const response: AxiosResponse<DesignDetails> | void = await Axios.get(
            `${environment.designerCatalogUrl}/designs/${designId}/details`,
            { headers }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        return response ? response.data : null;
    };

    /**
     * Opted-in brands for the catalog brand filter, searched server-side. The
     * endpoint used to return every opted-in brand at once, which PostgREST
     * silently truncated — brands past the cap were unselectable.
     */
    const fetchAvailableBrands: FetchAvailableBrands = async (q?: string, offset?: number, signal?: AbortSignal) => {
        const response: AxiosResponse<{ brands: CatalogBrand[]; total: number }> | void = await Axios.get(
            `${environment.designerCatalogUrl}/brands`,
            { headers, signal, params: { q: q || undefined, limit: 25, offset: offset ?? 0 } }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        return response ? response.data : { brands: [], total: 0 };
    };

    return [
        browseDesigns,
        catalogDesigns,
        { fetchBrowse, fetchCatalog, addDesign, removeDesign, fetchDesignDetails, fetchAvailableBrands }
    ];
};
